import React, { useState, useEffect } from "react";
import { packageApi } from "../../../api/packageApi";
import ConfirmModal from "../../../components/common/modals/ConfirmModal";
import { toast } from "react-toastify";
import "./PackageManager.css";

const getFeatureText = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  if (value === true) return "Included";
  return "";
};

const PackageManager = () => {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [packageToDelete, setPackageToDelete] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    tier: "Lite",
    price: "",
    currency: "ZAR",
    productLimit: "",
    features: {
      support: "",
      domain: "",
      ssl: "",
      analytics: "",
      marketing: "",
    },
    additionalFeatures: [],
    isActive: true,
    billingPeriod: "monthly",
    setupFee: 0,
  });

  // Default feature keys - removed "products" since we have Product Limit field
  const featureKeys = [
    { key: "support", label: "Support", placeholder: "e.g. Email Support" },
    { key: "domain", label: "Domain", placeholder: "e.g. Custom Domain" },
    { key: "ssl", label: "SSL", placeholder: "e.g. Included" },
    {
      key: "analytics",
      label: "Analytics",
      placeholder: "e.g. Basic Analytics",
    },
    {
      key: "marketing",
      label: "Marketing",
      placeholder: "e.g. Marketing Tools",
    },
  ];

  /* =========================================================
     LOAD PACKAGES
     ========================================================= */

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await packageApi.getPackages(false);

      if (response.success) {
        setPackages(response.data || []);
      } else if (Array.isArray(response)) {
        setPackages(response);
      } else if (response.packages) {
        setPackages(response.packages);
      } else {
        setPackages([]);
      }
    } catch (err) {
      console.error("Error loading packages:", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to load packages.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     FORM HANDLERS
     ========================================================= */

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleFeaturesChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      features: {
        ...prev.features,
        [name]: value,
      },
    }));
  };

  const handleAdditionalFeatureChange = (index, value) => {
    const updatedFeatures = [...formData.additionalFeatures];
    updatedFeatures[index] = value;
    setFormData((prev) => ({
      ...prev,
      additionalFeatures: updatedFeatures,
    }));
  };

  const addAdditionalFeature = () => {
    setFormData((prev) => ({
      ...prev,
      additionalFeatures: [...prev.additionalFeatures, ""],
    }));
  };

  const removeAdditionalFeature = (index) => {
    const updatedFeatures = formData.additionalFeatures.filter(
      (_, i) => i !== index,
    );
    setFormData((prev) => ({
      ...prev,
      additionalFeatures: updatedFeatures,
    }));
  };

  /* =========================================================
     CREATE / UPDATE
     ========================================================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");

      // Build features object with default and additional features
      const features = { ...formData.features };

      // Add additional features as key-value pairs
      formData.additionalFeatures.forEach((value, index) => {
        if (value && value.trim() !== "") {
          const key = `feature_${index + 1}`;
          features[key] = value.trim();
        }
      });

      // Filter out empty feature values
      const filteredFeatures = {};
      Object.keys(features).forEach((key) => {
        const value = getFeatureText(features[key]);
        if (value) {
          filteredFeatures[key] = value;
        }
      });

      const payload = {
        name: formData.name,
        tier: formData.tier,
        price: Number(formData.price),
        currency: formData.currency || "ZAR",
        productLimit:
          formData.productLimit === "" ? null : Number(formData.productLimit),
        features: filteredFeatures,
        isActive: formData.isActive,
        billingPeriod: formData.billingPeriod,
        setupFee: Number(formData.setupFee || 0),
      };

      if (editing) {
        await packageApi.updatePackage(editing.PackageID, payload);
        toast.success("Package updated successfully.");
      } else {
        await packageApi.createPackage(payload);
        toast.success("Package created successfully.");
      }

      await loadPackages();

      setShowForm(false);
      setEditing(null);
      resetForm();

    } catch (err) {
      console.error("Error saving package:", err);
      setError(
        err.response?.data?.error ||
          err.response?.data?.message ||
          "Failed to save package.",
      );
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     RESET FORM
     ========================================================= */

  const resetForm = () => {
    setFormData({
      name: "",
      tier: "Lite",
      price: "",
      currency: "ZAR",
      productLimit: "",
      features: {
        support: "",
        domain: "",
        ssl: "",
        analytics: "",
        marketing: "",
      },
      additionalFeatures: [],
      isActive: true,
      billingPeriod: "monthly",
      setupFee: 0,
    });
  };

  /* =========================================================
     EDIT
     ========================================================= */

  const handleEdit = (pkg) => {
    setEditing(pkg);

    let features = {
      support: "",
      domain: "",
      ssl: "",
      analytics: "",
      marketing: "",
    };
    let additionalFeatures = [];

    if (pkg.Features) {
      let featuresObj = pkg.Features;
      if (typeof pkg.Features === "string") {
        try {
          featuresObj = JSON.parse(pkg.Features);
        } catch (e) {
          console.warn("Could not parse features:", pkg.Features);
          featuresObj = {};
        }
      }

      if (typeof featuresObj === "object" && !Array.isArray(featuresObj)) {
        const defaultKeys = Object.keys(features);
        Object.keys(featuresObj).forEach((key) => {
          const value = getFeatureText(featuresObj[key]);
          if (value) {
            if (defaultKeys.includes(key)) {
              features[key] = value;
            } else {
              additionalFeatures.push(value);
            }
          }
        });
      }
    }

    setFormData({
      name: pkg.Name || "",
      tier: pkg.Tier || "Lite",
      price: pkg.Price ?? "",
      currency: pkg.Currency || "ZAR",
      productLimit: pkg.ProductLimit ?? "",
      features: features,
      additionalFeatures: additionalFeatures,
      isActive: pkg.IsActive ?? true,
      billingPeriod: pkg.BillingPeriod || "monthly",
      setupFee: pkg.SetupFee ?? 0,
    });

    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* =========================================================
     DELETE
     ========================================================= */

  const handleDelete = async () => {
    if (!packageToDelete) return;

    const packageId = packageToDelete.PackageID;
    setPackageToDelete(null);

    try {
      setLoading(true);
      setError("");

      await packageApi.deletePackage(packageId);

      toast.success("Package deleted successfully.");
      await loadPackages();

    } catch (err) {
      console.error("Error deleting package:", err);

      const errorMessage =
        err.response?.data?.error ||
        err.response?.data?.message ||
        "Failed to delete package. It may be in use by existing store requests or subscriptions.";

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  /* =========================================================
     FORMATTERS
     ========================================================= */

  const formatPrice = (price) => {
    return Number(price || 0).toLocaleString("en-ZA", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const formatSetupFee = (fee) => {
    return Number(fee || 0).toLocaleString("en-ZA", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const getTierClass = (tier) => {
    switch (String(tier || "").toLowerCase()) {
      case "starter":
        return "package-tier-starter";
      case "growth":
        return "package-tier-growth";
      case "pro":
        return "package-tier-pro";
      default:
        return "package-tier-lite";
    }
  };

  const getFeatureEntries = (features) => {
    if (!features) return [];

    let featuresObj = features;
    if (typeof features === "string") {
      try {
        featuresObj = JSON.parse(features);
      } catch (e) {
        return [];
      }
    }

    if (typeof featuresObj !== "object" || Array.isArray(featuresObj)) {
      return [];
    }

    return Object.entries(featuresObj)
      .map(([key, value]) => [key, getFeatureText(value)])
      .filter(([, value]) => value !== "")
      .map(([key, value]) => ({
        key: key,
        value,
        displayKey:
          key.charAt(0).toUpperCase() + key.slice(1).replace(/_/g, " "),
      }));
  };

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div className="package-manager">
      {/* =====================================================
          HEADER
          ===================================================== */}

      <div className="package-page-header">
        <div>
          <div className="package-eyebrow">
            <i className="fas fa-box-open"></i>
            PRODUCT MANAGEMENT
          </div>

          <h2>Package Manager</h2>

          <p>
            Create and manage the subscription packages available to Toora Flex
            clients.
          </p>
        </div>

        <button
          type="button"
          className="package-create-btn"
          onClick={() => {
            if (showForm) {
              setShowForm(false);
              setEditing(null);
              resetForm();
              return;
            }

            setShowForm(true);
            setEditing(null);
            resetForm();
          }}
        >
          <i className={`fas fa-${showForm ? "times" : "plus"}`}></i>

          {showForm ? "Cancel" : "New Package"}
        </button>
      </div>

      {/* =====================================================
          ALERTS
          ===================================================== */}

      {error && (
        <div className="package-alert package-alert-error">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      )}

      {/* =====================================================
          CREATE / EDIT FORM
          ===================================================== */}

      {showForm && (
        <div className="package-form-card">
          <div className="package-form-header">
            <div>
              <span className="package-form-label">PACKAGE CONFIGURATION</span>

              <h3>{editing ? "Edit Package" : "Create New Package"}</h3>

              <p>Configure pricing, limits and package features.</p>
            </div>

            <div className="package-form-icon">
              <i className="fas fa-sliders-h"></i>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {/* =================================================
                BASIC DETAILS
                ================================================= */}

            <div className="package-form-grid">
              <div className="package-field">
                <label htmlFor="name">Package Name</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g. Starter"
                  required
                />
              </div>

              <div className="package-field">
                <label htmlFor="tier">Package Tier</label>
                <select
                  id="tier"
                  name="tier"
                  value={formData.tier}
                  onChange={handleChange}
                >
                  <option value="Lite">Lite</option>
                  <option value="Starter">Starter</option>
                  <option value="Growth">Growth</option>
                  <option value="Pro">Pro</option>
                </select>
              </div>

              <div className="package-field">
                <label htmlFor="price">Monthly Price</label>
                <div className="package-input-prefix">
                  <span>R</span>
                  <input
                    type="number"
                    id="price"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    placeholder="499"
                    required
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>

              <div className="package-field">
                <label htmlFor="productLimit">Product Limit</label>
                <input
                  type="number"
                  id="productLimit"
                  name="productLimit"
                  value={formData.productLimit}
                  onChange={handleChange}
                  min="0"
                  placeholder="Leave empty for unlimited"
                />
              </div>

              <div className="package-field">
                <label htmlFor="billingPeriod">Billing Period</label>
                <select
                  id="billingPeriod"
                  name="billingPeriod"
                  value={formData.billingPeriod}
                  onChange={handleChange}
                >
                  <option value="monthly">Monthly</option>
                  <option value="quarterly">Quarterly</option>
                  <option value="yearly">Yearly</option>
                </select>
              </div>

              <div className="package-field">
                <label htmlFor="setupFee">Setup Fee</label>
                <div className="package-input-prefix">
                  <span>R</span>
                  <input
                    type="number"
                    id="setupFee"
                    name="setupFee"
                    value={formData.setupFee}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
            </div>

            {/* =================================================
                FEATURES
                ================================================= */}

            <div className="package-feature-editor">
              <div className="package-section-heading">
                <div>
                  <h4>Package Features</h4>
                  <p>Define the features displayed to clients.</p>
                </div>
                <i className="fas fa-list-check"></i>
              </div>

              <div className="package-feature-grid">
                {featureKeys.map(({ key, label, placeholder }) => (
                  <div className="package-feature-field" key={key}>
                    <label htmlFor={`feature-${key}`}>{label}</label>
                    <input
                      type="text"
                      id={`feature-${key}`}
                      name={key}
                      placeholder={placeholder}
                      value={formData.features[key] || ""}
                      onChange={handleFeaturesChange}
                    />
                  </div>
                ))}
              </div>

              {/* =================================================
                  ADDITIONAL FEATURES
                  ================================================= */}

              <div className="package-additional-features">
                <div className="package-additional-features-header">
                  <h5>Additional Features</h5>
                  <button
                    type="button"
                    className="package-add-feature-btn"
                    onClick={addAdditionalFeature}
                  >
                    <i className="fas fa-plus"></i>
                    Add Feature
                  </button>
                </div>

                {formData.additionalFeatures.length === 0 ? (
                  <div className="package-no-additional-features">
                    No additional features added. Click "Add Feature" to add
                    more.
                  </div>
                ) : (
                  formData.additionalFeatures.map((feature, index) => (
                    <div
                      className="package-additional-feature-item"
                      key={index}
                    >
                      <input
                        type="text"
                        placeholder={`Feature ${index + 1}`}
                        value={feature}
                        onChange={(e) =>
                          handleAdditionalFeatureChange(index, e.target.value)
                        }
                      />
                      <button
                        type="button"
                        className="package-remove-feature-btn"
                        onClick={() => removeAdditionalFeature(index)}
                      >
                        <i className="fas fa-trash"></i> Remove
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* =================================================
                ACTIVE TOGGLE
                ================================================= */}

            <label className="package-active-toggle">
              <input
                type="checkbox"
                name="isActive"
                checked={formData.isActive}
                onChange={handleChange}
              />

              <span className="package-toggle"></span>

              <span>
                <strong>Package Active</strong>
                <small>Make this package available to clients.</small>
              </span>
            </label>

            {/* =================================================
                FORM ACTIONS
                ================================================= */}

            <div className="package-form-actions">
              <button
                type="button"
                className="package-cancel-btn"
                onClick={() => {
                  setShowForm(false);
                  setEditing(null);
                  resetForm();
                }}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="package-save-btn"
                disabled={loading}
              >
                <i className="fas fa-save"></i>
                {loading
                  ? "Saving..."
                  : editing
                    ? "Update Package"
                    : "Create Package"}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* =====================================================
          PACKAGE LIST HEADER
          ===================================================== */}

      <div className="package-list-header">
        <div>
          <h3>Available Packages</h3>
          <span>
            {packages.length} package
            {packages.length !== 1 ? "s" : ""} configured
          </span>
        </div>

        <div className="package-list-count">
          <i className="fas fa-layer-group"></i>
          {packages.length}
        </div>
      </div>

      {/* =====================================================
          PACKAGE GRID
          ===================================================== */}

      {loading && packages.length === 0 ? (
        <div className="package-empty">
          <div className="package-empty-icon">
            <i className="fas fa-spinner fa-spin"></i>
          </div>
          <h3>Loading packages...</h3>
          <p>Please wait while we retrieve your package configuration.</p>
        </div>
      ) : (
        <div className="package-grid">
          {packages.length === 0 ? (
            <div className="package-empty">
              <div className="package-empty-icon">
                <i className="fas fa-box-open"></i>
              </div>
              <h3>No packages yet</h3>
              <p>
                Create your first subscription package to start offering plans
                to clients.
              </p>
              <button
                type="button"
                className="package-create-btn"
                onClick={() => {
                  setShowForm(true);
                  setEditing(null);
                  resetForm();
                }}
              >
                <i className="fas fa-plus"></i>
                Create Package
              </button>
            </div>
          ) : (
            packages.map((pkg) => {
              const featureEntries = getFeatureEntries(pkg.Features);

              return (
                <div
                  key={pkg.PackageID}
                  className={`package-card ${
                    !pkg.IsActive ? "package-card-inactive" : ""
                  } ${getTierClass(pkg.Tier)}`}
                >
                  {/* ===========================================
                      CARD HEADER
                      =========================================== */}

                  <div className="package-card-top">
                    <div className="package-tier-icon">
                      <i className="fas fa-cube"></i>
                    </div>

                    <div className="package-tier-info">
                      <span>{pkg.Tier || "Package"}</span>
                      <h3>{pkg.Name}</h3>
                    </div>

                    <span
                      className={`package-status ${
                        pkg.IsActive
                          ? "package-status-active"
                          : "package-status-inactive"
                      }`}
                    >
                      <span></span>
                      {pkg.IsActive ? "Active" : "Inactive"}
                    </span>
                  </div>

                  {/* ===========================================
                      MOST POPULAR
                      =========================================== */}

                  {String(pkg.Tier).toLowerCase() === "growth" && (
                    <div className="package-popular-badge">MOST POPULAR</div>
                  )}

                  {/* ===========================================
                      PRICE
                      =========================================== */}

                  <div className="package-price">
                    <span className="package-currency">R</span>
                    <strong>{formatPrice(pkg.Price)}</strong>
                    <span className="package-period">
                      / {pkg.BillingPeriod || "month"}
                    </span>
                  </div>

                  {/* ===========================================
                      PACKAGE SUMMARY
                      =========================================== */}

                  <div className="package-summary">
                    <div>
                      <i className="fas fa-box"></i>
                      <span>
                        <small>Products</small>
                        <strong>
                          {pkg.ProductLimit
                            ? `${pkg.ProductLimit} products`
                            : "Unlimited"}
                        </strong>
                      </span>
                    </div>
                    <div>
                      <i className="fas fa-wallet"></i>
                      <span>
                        <small>Setup Fee</small>
                        <strong>R{formatSetupFee(pkg.SetupFee)}</strong>
                      </span>
                    </div>
                  </div>

                  {/* ===========================================
                      FEATURES
                      =========================================== */}

                  <div className="package-features">
                    <div className="package-features-title">
                      Included Features
                    </div>

                    {featureEntries.length > 0 ? (
                      featureEntries.map(({ key, value, displayKey }) => (
                        <div className="package-feature" key={key}>
                          <span className="package-feature-check">
                            <i className="fas fa-check"></i>
                          </span>
                          <strong>{displayKey}</strong>
                          <small>{value}</small>
                        </div>
                      ))
                    ) : (
                      <div className="package-no-features">
                        No additional features configured.
                      </div>
                    )}
                  </div>

                  {/* ===========================================
                      ACTIONS
                      =========================================== */}

                  <div className="package-card-actions">
                    <button
                      type="button"
                      className="package-edit-btn"
                      onClick={() => handleEdit(pkg)}
                    >
                      <i className="fas fa-pen"></i>
                      Edit
                    </button>

                    <button
                      type="button"
                      className="package-delete-btn"
                      onClick={() => setPackageToDelete(pkg)}
                      title="Delete package"
                      aria-label={`Delete ${pkg.Name}`}
                    >
                      <i className="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      )}

      <ConfirmModal
        isOpen={Boolean(packageToDelete)}
        onClose={() => setPackageToDelete(null)}
        onConfirm={handleDelete}
        title="Delete package?"
        message={
          packageToDelete
            ? `Are you sure you want to delete "${packageToDelete.Name}"? This action cannot be undone.`
            : ""
        }
        confirmText="Delete Package"
        cancelText="Keep Package"
        variant="danger"
        icon={<i className="fas fa-trash" aria-hidden="true" />}
      />
    </div>
  );
};

export default PackageManager;
